import {
  ACCEPTED_FILE_TYPES,
  IMAGE_JPEG_QUALITY,
  IMAGE_LONG_EDGE_PX,
  LIBRARY_FILE_CAP_BYTES,
  REFUSED_FILE_MESSAGE,
} from '@/constants/app';

/**
 * What the Library takes, and what it does to it on the way in.
 *
 * Images are re-encoded to a sane size. Everything else — PDFs, slide decks,
 * documents — is stored byte for byte, because the point of keeping a file is
 * keeping the file. No format is converted on the way in: a .pptx that cannot
 * be laid out is still worth having, and the viewer reads out its text.
 */

export class RefusedFile extends Error {}

const EXTENSION_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',

  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ppt: 'application/vnd.ms-powerpoint',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  odp: 'application/vnd.oasis.opendocument.presentation',
  odt: 'application/vnd.oasis.opendocument.text',
  rtf: 'application/rtf',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
};

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

/** Browsers often report an empty type for HEIC, so the extension gets a say. */
export function detectType(file: { name: string; type: string }): string {
  if ((ACCEPTED_FILE_TYPES as readonly string[]).includes(file.type)) return file.type;
  return EXTENSION_TYPES[extensionOf(file.name)] ?? file.type ?? '';
}

export function isAccepted(type: string): boolean {
  return (ACCEPTED_FILE_TYPES as readonly string[]).includes(type);
}

export function isImage(type: string): boolean {
  return type.startsWith('image/');
}

export interface PreparedFile {
  blob: Blob;
  mime: string;
  name: string;
  /** Set when an image was re-encoded, for the message shown afterwards. */
  originalSize?: number;
}

export interface PrepareDeps {
  decode?: (blob: Blob) => Promise<ImageBitmap>;
  makeCanvas?: (w: number, h: number) => HTMLCanvasElement | OffscreenCanvas;
}

function defaultCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

async function toBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  quality: number,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The image could not be re-encoded.'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Long side capped, aspect kept, never enlarged. */
export function fitWithin(width: number, height: number, longEdge: number): { w: number; h: number } {
  const longest = Math.max(width, height);
  if (longest <= longEdge) return { w: width, h: height };
  const scale = longEdge / longest;
  return { w: Math.round(width * scale), h: Math.round(height * scale) };
}

/**
 * Checks the file, and for images re-encodes to JPEG at about 2000 px on the
 * long side. PDFs are left exactly as they are.
 */
export async function prepareFile(file: File, deps: PrepareDeps = {}): Promise<PreparedFile> {
  const mime = detectType(file);

  if (!isAccepted(mime)) {
    throw new RefusedFile(REFUSED_FILE_MESSAGE);
  }

  // Everything that is not an image is kept exactly as it arrived.
  if (!isImage(mime)) {
    if (file.size > LIBRARY_FILE_CAP_BYTES) {
      throw new RefusedFile(
        `That file is ${formatMb(file.size)}, over the ${formatMb(LIBRARY_FILE_CAP_BYTES)} limit.`,
      );
    }
    return { blob: file, mime, name: file.name };
  }

  const decode = deps.decode ?? ((blob: Blob) => createImageBitmap(blob));
  let bitmap: ImageBitmap;
  try {
    bitmap = await decode(file);
  } catch {
    // HEIC is the usual culprit: the spec allows it only where the browser can
    // actually decode it, and this browser cannot.
    throw new RefusedFile(
      `This browser cannot read ${extensionOf(file.name).toUpperCase() || 'that image'} files. Save it as JPEG or PNG first, then add it.`,
    );
  }

  const { w, h } = fitWithin(bitmap.width, bitmap.height, IMAGE_LONG_EDGE_PX);
  const canvas = (deps.makeCanvas ?? defaultCanvas)(w, h);
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
  if (!ctx) throw new RefusedFile('This browser could not process the image.');
  ctx.drawImage(bitmap as unknown as CanvasImageSource, 0, 0, w, h);
  bitmap.close?.();

  const blob = await toBlob(canvas, IMAGE_JPEG_QUALITY);

  if (blob.size > LIBRARY_FILE_CAP_BYTES) {
    throw new RefusedFile(
      `That image is still ${formatMb(blob.size)} after shrinking, over the ${formatMb(LIBRARY_FILE_CAP_BYTES)} limit.`,
    );
  }

  return {
    blob,
    mime: 'image/jpeg',
    name: renameToJpg(file.name),
    originalSize: file.size,
  };
}

function renameToJpg(name: string): string {
  const dot = name.lastIndexOf('.');
  const base = dot === -1 ? name : name.slice(0, dot);
  return `${base}.jpg`;
}

export function formatMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
